import { Dispatch } from 'redux';

import { action } from '../tools/reduxTools';
import { IState } from '../reducers';
import { Http } from '../tools/http';
import { ELcid } from '../entities/ILanguage';
import { ISortData } from '../components/Common/ColoredDatagrid/Sort';

export interface IDatatableSearchPayload {
    page: number;
    size: number;
    filters?: {
        field: string;
        filterPath?: string;
        fieldValueType?: string;
        value: {
            values?: any[];
            value?: string | boolean | number;
        };
    }[];
    lcid?: ELcid;
    orderBy?: ISortData[];
    searchQuery?: string;
}

export interface IDatatableSearchResults {
    facets: {
        [key: string]: {
            facetType: string;
        }[];
    };
    items: any[];
    size: number;
    page: number;
    count: number;
    itemsOnPageCount: number;
}

export interface IDatatableFieldsResult {
    field: string;
    properties: {
        facetPath: string;
        facetType: string;
        filterFieldType: string;
        filterPath: string;
        propertyName: string;
        sortPath: string;
        isFacet: boolean;
        isSortable: boolean;
        isFilterable: boolean;
    }[];
}

export enum EDatatableKeys {
    VALIDATION_REQUEST = 'ValidationRequest',
    KEY_DATES = 'KeyDates',
    CAMPAIGN = 'Campaign',
    CAMPAIGN_RECIPIENT = 'CampaignRecipient',
    DISPUTE = 'Dispute',
    EXPLORE = 'Explore',
    DISPUTE_INSTANCE = 'DisputeInstance',
    QUESTION_ANSWER = 'QuestionAnswer',
    INQUIRY_PRODUCT_INFORMATION = 'ExportControlInquiries',
    EXPORT_CONTROL_DETAILS = 'ExportControlDetails',
    RESOURCE_ACTION = 'ResourceAction',
    SUBSTITUTION = 'Substitution',
    TPRM_ENTITY = 'TPRMEntity',
    TPRM_REVIEW = 'TPRMReview',
    ASSIGNED_ROLES = 'AssignedRoles',
    MANDATE = 'Mandate'
}

/** Inner datatable keys for admin-restricted AssignedRoles searches (SMH InnerIndexKeys). */
export enum EInnerDatatableKeys {
    PRINCIPAL_PERMISSION = 'PrincipalPermission',
    USERS_IN_PLATFORM = 'UsersInPlatform'
}

export enum EDatatableScope {
    ORGANIZATIONS = 'Organization',
    PROFILES = 'User',
    CLUSTER = 'Cluster',
    VALIDATION_REQUEST = 'ValidationRequest',
    PLATFORM  = 'Platform',
    NORMATIVE_DOCUMENTATION = 'NormativeDocumentation',
    LEGAL_ENTITY = 'LegalEntity',
    GROUP = 'Group',
    LEGAL_DATE = 'LegalDate',
    CAMPAIGN = 'Campaign',
    LINKED_OBJECT = 'LinkedObject'
}

export const searchDatatableByKey = (scope: EDatatableScope, key: EDatatableKeys, scopeId: string, payload: IDatatableSearchPayload, mockedData?: boolean) => action<Promise<IDatatableSearchResults>>((dispatch: Dispatch, getState: () => IState, http: Http) => {
    const { language } = getState();
    return http.commonApiPost(`/data-table/search/${scope}/${scopeId}/${key}?lcid=${language.userLanguage || ELcid.Fr}${mockedData ? `&useMockedData=true` : ''}`, payload).then((res: IDatatableSearchResults) => {
        return res;
    });
});

export const searchDatatableFieldsByKey = (scope: EDatatableScope, key: EDatatableKeys, scopeId: string) => action<Promise<IDatatableFieldsResult[]>>((dispatch: Dispatch, getState: () => IState, http: Http) => {
    return http.commonApiGet(`/data-table/search/${scope}/${scopeId}/${key}/Fields`).then(res => res).catch(err => err);
});

export const searchDatatableByKeyRestricted = (
    scope: EDatatableScope,
    key: EDatatableKeys,
    scopeId: string,
    innerKey: EInnerDatatableKeys,
    payload: IDatatableSearchPayload,
    mockedData?: boolean
) => action<Promise<IDatatableSearchResults>>((dispatch: Dispatch, getState: () => IState, http: Http) => {
    const { language } = getState();
    return http.commonApiPost(
        `/data-table/search/${scope}/${scopeId}/${key}/${innerKey}/restricted-access?lcid=${language.userLanguage || ELcid.Fr}${mockedData ? `&useMockedData=true` : ''}`,
        payload
    ).then((res: IDatatableSearchResults) => res);
});

export const searchDatatableFieldsByKeyRestricted = (
    scope: EDatatableScope,
    key: EDatatableKeys,
    scopeId: string,
    innerKey: EInnerDatatableKeys
) => action<Promise<IDatatableFieldsResult[]>>((dispatch: Dispatch, getState: () => IState, http: Http) => {
    return http.commonApiGet(`/data-table/search/${scope}/${scopeId}/${key}/${innerKey}/restricted-access/Fields`).then(res => res).catch(err => err);
});

export const exampleStructures = () => action<Promise<any>>((dispatch: Dispatch, getState: () => IState, http: Http) => {
    return http.commonApiGet(`/data-table/example-structures`);
});
